import React, { useState, useEffect } from 'react';
import { EventCard } from './EventCard';
import { EventGridSkeleton } from './EventCardSkeleton';
import { Skeleton } from './ui/skeleton';
import { Button } from './ui/button';
import { RefreshCw, Calendar, Ticket, AlertCircle, WifiOff } from 'lucide-react';
import { apiService, UpcomingEvent, ApiService } from '../services/api';

export function EventGrid() {
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Load events from API - NO FALLBACK
  const loadEvents = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🎭 [EVENT_GRID] Loading events...');
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Tempo limite excedido')), 8000)
      );
      
      const apiPromise = apiService.getUpcomingEvents();
      
      const response = await Promise.race([apiPromise, timeoutPromise]);
      
      
      if (response.events && Array.isArray(response.events) && response.events.length > 0) {
        console.log(`🎭 [EVENT_GRID] Loaded ${response.events.length} events from API`);
        setEvents(response.events);
        setError(null);
      } else {
        console.log('🎭 [EVENT_GRID] API returned empty or invalid data');
        setError('Nenhum evento encontrado no momento');
        setEvents([]);
      }
      
    } catch (err) {
      console.log('🎭 [EVENT_GRID] Error loading events:', err.message);
      const errorMessage = err.message === 'Tempo limite excedido' 
        ? 'Conexão muito lenta. Verifique sua internet e tente novamente.'
        : 'Erro ao conectar com o servidor. Tente novamente em alguns instantes.';
      
      setError(errorMessage);
      setEvents([]);
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  // Retry function
  const handleRetry = async () => {
    setIsRetrying(true);
    await loadEvents();
  };

  // Loading state
  if (isLoading) {
    return (
      <section className="pt-6 pb-8 lg:pt-8 lg:pb-12 bg-gradient-to-b from-muted/30 to-background">
        <div className="container-modern">
          {/* Elegant Loading Header */}
          <div className="text-center mb-16">
            <div className="glass-subtle inline-block p-8 rounded-3xl mb-8 border shadow-medium">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-gradient-orange-elegant rounded-2xl shadow-large">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              <Skeleton className="h-10 w-80 mx-auto mb-4 rounded-xl" />
              <Skeleton className="h-6 w-96 mx-auto mb-8 rounded-lg" />
              <div className="flex items-center justify-center space-x-8">
                <Skeleton className="h-5 w-32 rounded-lg" />
                <Skeleton className="h-5 w-32 rounded-lg" />
              </div>
            </div>
          </div>
          
          {/* Enhanced Grid Skeleton */}
          <EventGridSkeleton count={9} />
        </div>
      </section>
    );
  }

  // Error state - show friendly error message with retry
  if (error || events.length === 0) {
    return (
      <section className="pt-6 pb-8 lg:pt-8 lg:pb-12 bg-gradient-to-b from-muted/30 to-background">
        <div className="container-modern">
          <div className="text-center max-w-lg mx-auto">
            <div className="card-elegant p-12">
              <div className="flex items-center justify-center w-20 h-20 mx-auto mb-8 bg-orange-50 dark:bg-orange-950/20 rounded-3xl border border-orange-200 dark:border-orange-800/30">
                <WifiOff className="w-10 h-10 text-orange-500" />
              </div>
              <h2 className="mb-6 text-orange-600 dark:text-orange-400">
                Não foi possível carregar os eventos
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {error || 'Ocorreu um problema ao buscar os eventos. Verifique sua conexão com a internet e tente novamente.'}
              </p>
              <div className="space-y-4">
                <Button 
                  onClick={handleRetry}
                  disabled={isRetrying}
                  className="btn-modern btn-primary-modern px-8 py-3 w-full sm:w-auto"
                >
                  {isRetrying ? (
                    <>
                      <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
                      Tentando conectar...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-5 w-5" />
                      Tentar Novamente
                    </>
                  )}
                </Button>
                
                {/* Connection status */}
                <div className="text-xs text-muted-foreground">
                  <div className="flex items-center justify-center space-x-2">
                    <WifiOff className="w-3 h-3" />
                    <span>Verifique sua conexão e tente novamente</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative pt-6 pb-8 lg:pt-8 lg:pb-12 overflow-hidden bg-gradient-to-b from-muted/30 to-background">
      {/* Subtle Background Enhancement */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-primary/[0.02] via-transparent to-orange-primary/[0.01]"></div>
      
      <div className="container-modern relative">
        {/* Clean Minimalist Header with Mobile Spacing */}
        <div className="text-center mb-12 sm:mb-10">
          {/* Simple Badge with Lines */}
          <div className="flex items-center justify-center mb-6">
            <div className="clean-header-container">
              {/* Left Line */}
              <div className="header-line"></div>
              
              {/* Central Badge */}
              <div className="clean-badge">
                <Ticket className="clean-icon" />
              </div>
              
              {/* Right Line */}
              <div className="header-line"></div>
            </div>
          </div>
          
          {/* Simple Title */}
          <h2 className="clean-title mb-6 sm:mb-0">
            Eventos Disponíveis
          </h2>
        </div>
        
        {/* Event Grid with Proper Responsive Layout */}
        <div className="relative">
          <div className="event-grid-responsive">
            {events.map((event, index) => (
              <div 
                key={`${event.eventId}-${index}`}
                className="group animate-fade-in-up hover-lift-subtle"
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  animationFillMode: 'both'
                }}
              >
                {/* Enhanced Card Wrapper with Perfect Containment */}
                <div className="relative h-full overflow-hidden rounded-3xl">
                  {/* Perfectly Contained Glow Effect */}
                  <div className="absolute inset-2 bg-gradient-orange-glow opacity-0 group-hover:opacity-100 blur-md transition-all duration-500 rounded-2xl pointer-events-none"></div>
                  
                  {/* Event Card with Full Height */}
                  <div className="relative z-10 h-full">
                    <EventCard event={event} />
                  </div>
                  
                  {/* Elegant Corner Indicator */}
                  <div className="absolute top-2 right-2 w-2 h-2 bg-gradient-orange-elegant rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-sm z-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Clean Minimalist Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* ===== CLEAN MINIMALIST HEADER SYSTEM ===== */
          
          /* Main header container */
          .clean-header-container {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 1.5rem;
            width: 100%;
            max-width: 300px;
            margin: 0 auto;
          }
          
          /* Subtle separator lines */
          .header-line {
            height: 1px;
            background: linear-gradient(
              90deg, 
              transparent 0%, 
              rgba(255, 140, 0, 0.2) 50%, 
              transparent 100%
            );
            flex: 1;
            min-width: 40px;
          }
          
          /* Clean central badge */
          .clean-badge {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 48px;
            height: 48px;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(12px) saturate(150%);
            border: 1px solid rgba(255, 140, 0, 0.15);
            border-radius: 50%;
            box-shadow: 
              0 2px 8px rgba(255, 140, 0, 0.1),
              0 1px 4px rgba(0, 0, 0, 0.05),
              inset 0 1px 2px rgba(255, 255, 255, 0.3);
            transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
          }
          
          /* Dark mode badge */
          .dark .clean-badge {
            background: rgba(26, 26, 26, 0.95);
            border: 1px solid rgba(255, 140, 0, 0.2);
            box-shadow: 
              0 2px 8px rgba(255, 140, 0, 0.15),
              0 1px 4px rgba(0, 0, 0, 0.2),
              inset 0 1px 2px rgba(255, 255, 255, 0.1);
          }
          
          /* Icon styling */
          .clean-icon {
            width: 20px;
            height: 20px;
            color: #FF8C00;
            transition: all 300ms ease;
          }
          
          /* Subtle hover effects */
          .clean-badge:hover {
            transform: translateY(-1px) scale(1.05);
            box-shadow: 
              0 4px 12px rgba(255, 140, 0, 0.15),
              0 2px 6px rgba(0, 0, 0, 0.08),
              inset 0 1px 2px rgba(255, 255, 255, 0.4);
            border-color: rgba(255, 140, 0, 0.25);
          }
          
          .clean-badge:hover .clean-icon {
            transform: scale(1.1);
            color: #FF7A00;
          }
          
          /* Clean title styling */
          .clean-title {
            font-size: 1.75rem;
            font-weight: 600;
            line-height: 1.2;
            font-family: 'Poppins', sans-serif;
            letter-spacing: -0.02em;
            color: var(--foreground);
            margin: 0;
          }
          
          /* Dark mode line enhancement */
          .dark .header-line {
            background: linear-gradient(
              90deg, 
              transparent 0%, 
              rgba(255, 140, 0, 0.15) 50%, 
              transparent 100%
            );
          }
          
          /* ===== RESPONSIVE BREAKPOINTS ===== */
          
          /* Small mobile (up to 374px) */
          @media (max-width: 374px) {
            .clean-header-container {
              gap: 1rem;
              max-width: 250px;
            }
            
            .header-line {
              min-width: 30px;
            }
            
            .clean-badge {
              width: 40px;
              height: 40px;
            }
            
            .clean-icon {
              width: 16px;
              height: 16px;
            }
            
            .clean-title {
              font-size: 1.5rem;
            }
          }
          
          /* Standard mobile (375px - 479px) */
          @media (min-width: 375px) and (max-width: 479px) {
            .clean-header-container {
              gap: 1.25rem;
              max-width: 280px;
            }
            
            .header-line {
              min-width: 35px;
            }
          }
          
          /* Large mobile (480px - 639px) */
          @media (min-width: 480px) and (max-width: 639px) {
            .clean-header-container {
              gap: 1.5rem;
              max-width: 320px;
            }
            
            .header-line {
              min-width: 50px;
            }
            
            .clean-badge {
              width: 52px;
              height: 52px;
            }
            
            .clean-icon {
              width: 22px;
              height: 22px;
            }
            
            .clean-title {
              font-size: 1.875rem;
            }
          }
          
          /* Tablet (640px - 1023px) */
          @media (min-width: 640px) and (max-width: 1023px) {
            .clean-header-container {
              gap: 2rem;
              max-width: 380px;
            }
            
            .header-line {
              min-width: 70px;
            }
            
            .clean-badge {
              width: 56px;
              height: 56px;
            }
            
            .clean-icon {
              width: 24px;
              height: 24px;
            }
            
            .clean-title {
              font-size: 2rem;
            }
          }
          
          /* Desktop (1024px+) */
          @media (min-width: 1024px) {
            .clean-header-container {
              gap: 2.5rem;
              max-width: 450px;
            }
            
            .header-line {
              min-width: 100px;
            }
            
            .clean-badge {
              width: 60px;
              height: 60px;
            }
            
            .clean-icon {
              width: 26px;
              height: 26px;
            }
            
            .clean-title {
              font-size: 2.25rem;
            }
          }
          
          /* ===== ACCESSIBILITY ===== */
          
          /* Reduced motion support */
          @media (prefers-reduced-motion: reduce) {
            .clean-badge,
            .clean-icon {
              transition: none !important;
            }
            
            .clean-badge:hover,
            .clean-badge:hover .clean-icon {
              transform: none !important;
            }
          }
          
          /* High contrast mode */
          @media (prefers-contrast: high) {
            .clean-badge {
              border-width: 2px;
              border-color: rgba(255, 140, 0, 0.8);
            }
            
            .header-line {
              background: linear-gradient(
                90deg, 
                transparent 0%, 
                rgba(255, 140, 0, 0.6) 50%, 
                transparent 100%
              );
              height: 2px;
            }
          }
          
          /* Focus states for accessibility */
          .clean-badge:focus-within {
            outline: 2px solid rgba(255, 140, 0, 0.6);
            outline-offset: 2px;
          }
          
          /* Touch-friendly sizing */
          @media (hover: none) and (pointer: coarse) {
            .clean-badge {
              min-width: 44px;
              min-height: 44px;
            }
            
            .clean-badge:active {
              transform: scale(0.95);
              transition-duration: 100ms;
            }
          }
        `
      }} />
    </section>
  );
}